import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import questionnairesService from '../../../services/questionnairesService';
import useNotifications from '../../../hooks/useNotifications';

const MillionaireGame = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);
  const [hasWon, setHasWon] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quizTitle, setQuizTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Opción elegida en la pregunta actual; mientras no sea null se ignoran nuevos clics
  const [selectedOption, setSelectedOption] = useState(null);
  const advanceTimeoutRef = useRef(null);
  
  // Get courseId from location state or URL params
  const courseId = location.state?.courseId || new URLSearchParams(location.search).get('courseId');
  const isPreview = new URLSearchParams(location.search).get('preview') === '1';
  const usuarioConCedula = JSON.parse(localStorage.getItem("usuarioConCedula") || "{}");
  
  // Notifications hook
  const { refetch: refetchNotifications } = useNotifications(
    `/api/Notification/${usuarioConCedula.id}`,
    `${process.env.REACT_APP_LOCATION}/notificationHub`,
    `${process.env.REACT_APP_LOCATION}`
  );

  useEffect(() => {
    let cancelled = false;

    // La ruta reutiliza el componente al cambiar de cuestionario: hay que reiniciar todo el estado
    clearTimeout(advanceTimeoutRef.current);
    setQuestions([]);
    setQuizTitle('');
    setCurrentIndex(0);
    setIsGameOver(false);
    setHasWon(false);
    setSubmitting(false);
    setSelectedOption(null);
    setLoading(true);

    const fetchQuiz = async () => {
      try {
        const data = await questionnairesService.getQuestionnaireById(id);
        if (cancelled) return;
        setQuestions(data.questions || []);
        setQuizTitle(data.title);
      } catch (error) {
        console.error('Error fetching quiz:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchQuiz();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => () => clearTimeout(advanceTimeoutRef.current), []);

  const submitAnswers = async () => {
    if (isPreview) {
      return;
    }

    if (!courseId || !usuarioConCedula?.id) {
      console.warn('Missing courseId or userId for questionnaire submission');
      return;
    }

    try {
      setSubmitting(true);
      
      // Prepare answers array
      const answers = questions.map((question, index) => ({
        questionId: question.id,
        selectedAnswer: question.correctAnswer // Since they won, all answers are correct
      }));

      // Submit answers to update course progress
      const result = await questionnairesService.submitQuestionnaireAnswers(
        parseInt(id), // questionnaireId
        usuarioConCedula.id, // userId
        courseId, // courseId
        answers
      );

      console.log('Questionnaire answers submitted successfully:', result);
      
      // Trigger notification refresh to show course completion notification
      try {
        await refetchNotifications();
        console.log('Notifications refreshed after questionnaire completion');
      } catch (notificationError) {
        console.error('Error refreshing notifications:', notificationError);
      }
    } catch (error) {
      console.error('Error submitting questionnaire answers:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const ANSWER_FEEDBACK_MS = 900;

  const handleAnswer = (optionKey, event) => {
    event?.currentTarget?.blur();
    if (selectedOption !== null) return;

    const currentQuestion = questions[currentIndex];
    const isCorrect = optionKey === currentQuestion.correctAnswer;
    setSelectedOption(optionKey);

    advanceTimeoutRef.current = setTimeout(() => {
      if (isCorrect) {
        if (currentIndex + 1 === questions.length) {
          setHasWon(true);
          // Submit answers when game is won
          submitAnswers();
        } else {
          setCurrentIndex((prev) => prev + 1);
        }
      } else {
        setIsGameOver(true);
        // Submit partial answers when game is over
        submitPartialAnswers(optionKey);
      }
      setSelectedOption(null);
    }, ANSWER_FEEDBACK_MS);
  };

  const submitPartialAnswers = async (wrongAnswer) => {
    if (!courseId || !usuarioConCedula?.id) {
      console.warn('Missing courseId or userId for questionnaire submission');
      return;
    }

    try {
      setSubmitting(true);
      
      // Prepare answers array with correct answers up to current question
      const answers = questions.slice(0, currentIndex).map((question, index) => ({
        questionId: question.id,
        selectedAnswer: question.correctAnswer // All previous answers were correct
      }));
      
      // Add the wrong answer for the current question
      answers.push({
        questionId: questions[currentIndex].id,
        selectedAnswer: wrongAnswer
      });

      // Submit answers to update course progress
      const result = await questionnairesService.submitQuestionnaireAnswers(
        parseInt(id), // questionnaireId
        usuarioConCedula.id, // userId
        courseId, // courseId
        answers
      );

      console.log('Partial questionnaire answers submitted successfully:', result);
      
      // Trigger notification refresh to show progress updates
      try {
        await refetchNotifications();
        console.log('Notifications refreshed after partial questionnaire submission');
      } catch (notificationError) {
        console.error('Error refreshing notifications:', notificationError);
      }
    } catch (error) {
      console.error('Error submitting partial questionnaire answers:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const restartGame = () => {
    clearTimeout(advanceTimeoutRef.current);
    setSelectedOption(null);
    setCurrentIndex(0);
    setIsGameOver(false);
    setHasWon(false);
    setSubmitting(false); // Reset submission status as well
  };

  const progressPercentage = ((currentIndex + (hasWon ? 1 : 0)) / questions.length) * 100;

  if (loading) return <div className="text-center p-4 text-gray-700">Cargando...</div>;
  if (questions.length === 0) return <div className="text-center p-4 text-red-500">No hay preguntas disponibles.</div>;

  const question = questions[currentIndex];
  if (!question) return <div className="text-center p-4 text-red-500">Pregunta no encontrada.</div>;

  if (isGameOver)
    return (
      <div className="text-center p-10 border border-red-400 bg-red-50 rounded shadow max-w-xl mx-auto mt-10">
        <h2 className="text-2xl font-bold text-red-700 mb-4">❌ ¡Juego terminado!</h2>
        <p className="text-gray-700 mb-4">Respuesta incorrecta. Inténtalo de nuevo.</p>
        {submitting ? (
          <div className="text-sm text-gray-600 mb-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-400 mx-auto mb-2"></div>
            Guardando progreso...
          </div>
        ) : (
          <p className="text-sm text-red-600 mb-4">✅ Progreso guardado</p>
        )}
        <div className="flex gap-3 justify-center">
          <button
            onClick={restartGame}
            className="px-6 py-2 background-green-aqua rounded shadow hover:opacity-90 transition-all"
          >
            Reintentar
          </button>
          {courseId && (
            <button
              onClick={() => navigate(`/courses/Introcourse/${courseId}`)}
              className="px-6 py-2 bg-gray-500 text-white rounded shadow hover:opacity-90 transition-all"
            >
              Volver al Curso
            </button>
          )}
        </div>
      </div>
    );

  if (hasWon)
    return (
      <div className="text-center p-10 border border-green-aqua bg-green-50 rounded shadow max-w-xl mx-auto mt-10">
        <h2 className="text-2xl font-bold text-green-aqua mb-4">🎉 ¡Felicidades!</h2>
        <p className="text-gray-700 mb-4">Has ganado. Todas las respuestas fueron correctas.</p>
        {submitting ? (
          <div className="text-sm text-gray-600 mb-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-aqua mx-auto mb-2"></div>
            Actualizando progreso del curso...
          </div>
        ) : (
          <p className="text-sm text-green-600 mb-4">✅ Progreso del curso actualizado</p>
        )}
        {courseId && (
          <button
            onClick={() => navigate(`/courses/Introcourse/${courseId}`)}
            className="px-6 py-2 background-green-aqua rounded shadow hover:opacity-90 transition-all"
          >
            Volver al Curso
          </button>
        )}
      </div>
    );

  return (
    <div className="max-w-2xl mx-auto mt-10 p-6 border border-green-aqua rounded-xl shadow-lg bg-white">
      {/* Título */}
      <h1 className="text-3xl font-extrabold text-center text-green-aqua mb-6">{quizTitle}</h1>

      {/* Barra de progreso */}
      <div className="mb-6">
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          <div
            className="background-green-light h-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        <p className="text-sm text-center text-gray-500 mt-1">
          Pregunta {currentIndex + 1} de {questions.length}
        </p>
      </div>

      {/* Pregunta */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800 text-center">{question.questionText}</h2>
      </div>

      {/* Opciones */}
      <div className="grid grid-cols-2 gap-4">
        {['A', 'B', 'C', 'D'].map((opt) => {
          const isSelected = selectedOption === opt;
          const isCorrectSelection = isSelected && opt === question.correctAnswer;
          const stateClass = isSelected
            ? isCorrectSelection
              ? 'bg-green-600 text-white ring-4 ring-green-300 scale-[1.02]'
              : 'bg-red-600 text-white ring-4 ring-red-300 scale-[1.02]'
            : selectedOption !== null
            ? 'background-green-aqua opacity-50'
            : 'background-green-aqua hover:opacity-90';

          return (
            <button
              // La key incluye la pregunta para que los botones se recreen y no arrastren foco/hover de la anterior
              key={`${question.id ?? currentIndex}-${opt}`}
              type="button"
              onClick={(event) => handleAnswer(opt, event)}
              disabled={selectedOption !== null}
              className={`p-4 font-semibold rounded-lg shadow-md transition-all focus:outline-none disabled:cursor-not-allowed ${stateClass}`}
            >
              <span className="font-bold">{opt})</span> {question[`option${opt}`]}
            </button>
          );
        })}
      </div>

      <div className="h-6 mt-4 text-center font-semibold">
        {selectedOption !== null &&
          (selectedOption === question.correctAnswer ? (
            <span className="text-green-600">¡Correcto!</span>
          ) : (
            <span className="text-red-600">Respuesta incorrecta</span>
          ))}
      </div>
    </div>
  );
};

export default MillionaireGame;
